import React, { useState, useEffect } from 'react';
import { 
  X, Users, Clock, Check, XCircle, Crown, Shield, 
  ChevronLeft, ChevronRight, UserCheck, UserX, 
  Calendar, MessageCircle, AlertCircle, CheckCircle,
  Eye, EyeOff 
} from 'lucide-react';
import API_URL from '../config/ApiUrl';
import { useTheme } from '../arrangement/ThemeContext';

export function PendingRoomsManagementModal({ isOpen, onClose, currentUser, showNotification }) {
  const [activeTab, setActiveTab] = useState('incoming'); // 'incoming' or 'outgoing'
  const [incomingRequests, setIncomingRequests] = useState([]);
  const [outgoingRequests, setOutgoingRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [processingRequest, setProcessingRequest] = useState(null);

  const { colors } = useTheme(); 
  // Fetch incoming requests (for rooms user owns/admins)
  const fetchIncomingRequests = async () => {
    try {
      const response = await fetch(`${API_URL}/api/rooms/manage-requests/incoming`, {
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success) {
        setIncomingRequests(data.requests);
      }
    } catch (error) {
      console.error('Failed to fetch incoming requests:', error);
    }
  };

  // Fetch outgoing requests (user's pending requests)
  const fetchOutgoingRequests = async () => {
    try {
      const response = await fetch(`${API_URL}/api/rooms/manage-requests/outgoing`, {
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success) {
        setOutgoingRequests(data.requests);
      }
    } catch (error) {
      console.error('Failed to fetch outgoing requests:', error);
    }
  };

  useEffect(() => {
    if (isOpen) {
      setLoading(true);
      Promise.all([
        fetchIncomingRequests(),
        fetchOutgoingRequests()
      ]).finally(() => setLoading(false));
    }
  }, [isOpen]);

  // Handle incoming request (accept/deny)
  const handleIncomingRequest = async (requestId, action) => {
    setProcessingRequest(requestId);
    try {
      const response = await fetch(`${API_URL}/api/rooms/manage-requests/${requestId}/${action}`, {
        method: 'POST',
        credentials: 'include'
      });
      
      const data = await response.json();
      if (data.success) {
        showNotification(data.message);
        await fetchIncomingRequests(); // Refresh the list
      } else {
        showNotification(data.error || 'Failed to process request', 'error');
      }
    } catch (error) {
      showNotification('Failed to process request', 'error');
    } finally {
      setProcessingRequest(null);
    }
  };

  // Cancel outgoing request
  const cancelOutgoingRequest = async (requestId) => {
    setProcessingRequest(requestId);
    try {
      const response = await fetch(`${API_URL}/api/rooms/manage-requests/${requestId}/cancel`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      const data = await response.json();
      if (data.success) {
        showNotification(data.message);
        await fetchOutgoingRequests(); // Refresh the list
      } else {
        showNotification(data.error || 'Failed to cancel request', 'error');
      }
    } catch (error) {
      showNotification('Failed to cancel request', 'error');
    } finally {
      setProcessingRequest(null);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div style={{
        backgroundColor: '#1a1a1a',
        borderRadius: '20px',
        border: '1px solid #333',
        width: '100%',
        maxWidth: '800px',
        maxHeight: '90vh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Header */}
        <div style={{
          padding: '24px',
          borderBottom: `1px solid ${colors.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div>
            <h2 style={{
              margin: 0,
              fontSize: '24px',
              fontWeight: '700',
              color: '#e5e5e5'
            }}>
              Pending Room Requests
            </h2>
            <p style={{
              margin: '4px 0 0 0',
              color: '#888',
              fontSize: '14px'
            }}>
              Manage access requests for your rooms
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              padding: '8px',
              backgroundColor: 'transparent',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              color: '#888',
              transition: 'all 0.2s ease'
            }}
          >
            <X size={24} />
          </button>
        </div>

        {/* Tab Navigation */}
        <div style={{
          display: 'flex',
          borderBottom: '1px solid #333'
        }}>
          <button
            onClick={() => setActiveTab('incoming')}
            style={{
              flex: 1,
              padding: '16px 24px',
              backgroundColor: activeTab === 'incoming' ? '#2a2a2a' : 'transparent',
              border: 'none',
              borderBottom: activeTab === 'incoming' ? '2px solid #f59e0b' : '2px solid transparent',
              color: activeTab === 'incoming' ? '#f59e0b' : '#888',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}
          >
            <UserCheck size={18} />
            Your Room Requests ({incomingRequests.length})
          </button>
          <button
            onClick={() => setActiveTab('outgoing')}
            style={{
              flex: 1,
              padding: '16px 24px',
              backgroundColor: activeTab === 'outgoing' ? '#2a2a2a' : 'transparent',
              border: 'none',
              borderBottom: activeTab === 'outgoing' ? '2px solid #8b5cf6' : '2px solid transparent',
              color: activeTab === 'outgoing' ? '#8b5cf6' : '#888',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}
          >
            <Clock size={18} />
            Requested Rooms ({outgoingRequests.length})
          </button>
        </div>

        {/* Content */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: '24px'
        }}>
          {loading ? (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '60px',
              color: '#888'
            }}>
              <div style={{
                width: '40px',
                height: '40px',
                border: '4px solid #333',
                borderTop: '4px solid #f59e0b',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                marginRight: '16px'
              }} />
              Loading requests...
            </div>
          ) : (
            <>
              {/* Incoming Requests Tab */}
              {activeTab === 'incoming' && (
                <div>
                  {incomingRequests.length === 0 ? (
                    <div style={{
                      textAlign: 'center',
                      padding: '60px 20px',
                      color: '#888'
                    }}>
                      <UserCheck size={48} style={{ marginBottom: '16px', opacity: 0.3 }} />
                      <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: '600' }}>
                        No pending requests
                      </h3>
                      <p style={{ margin: 0, fontSize: '14px' }}>
                        No one has requested access to your rooms yet
                      </p>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                      {incomingRequests.map((request) => (
                        <div key={request.id} style={{
                          background: colors.bgCard,
                          border: '1px solid #333',
                          borderRadius: '12px',
                          padding: '20px',
                          transition: 'all 0.3s ease'
                        }}>
                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            marginBottom: '16px'
                          }}>
                            <div style={{ flex: 1 }}>
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px',
                                marginBottom: '8px'
                              }}>
                                <img
                                  src={request.user?.avatar_url || `https://api.dicebear.com/7.x/initials/svg?seed=${request.user?.username}`}
                                  alt={request.user?.username}
                                  style={{
                                    width: '40px',
                                    height: '40px',
                                    borderRadius: '50%',
                                    objectFit: 'cover'
                                  }}
                                />
                                <div>
                                  <h4 style={{
                                    margin: 0,
                                    fontSize: '16px',
                                    fontWeight: '600',
                                    color: '#e5e5e5'
                                  }}>
                                    {request.user?.username}
                                  </h4>
                                  <p style={{
                                    margin: 0,
                                    fontSize: '12px',
                                    color: '#888',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px'
                                  }}>
                                    <Calendar size={10} />
                                    {new Date(request.joined_at).toLocaleDateString()}
                                  </p>
                                </div>
                              </div>
                              <div style={{
                                padding: '12px',
                                background: colors.bgTertiary,
                                borderRadius: '8px',
                                marginBottom: '12px'
                              }}>
                                <p style={{
                                  margin: '0 0 8px 0',
                                  fontSize: '14px',
                                  fontWeight: '600',
                                  color: '#f59e0b'
                                }}>
                                  Room: {request.room?.title}
                                </p>
                                <p style={{
                                  margin: 0,
                                  fontSize: '14px',
                                  color: '#e5e5e5',
                                  lineHeight: '1.4'
                                }}>
                                  "{request.request_message}"
                                </p>
                              </div>
                            </div>
                          </div>
                          
                          <div style={{
                            display: 'flex',
                            gap: '12px',
                            justifyContent: 'flex-end'
                          }}>
                            <button
                              onClick={() => handleIncomingRequest(request.id, 'deny')}
                              disabled={processingRequest === request.id}
                              style={{
                                padding: '8px 16px',
                                background: '#ef4444',
                                border: 'none',
                                borderRadius: '8px',
                                color: 'white',
                                fontSize: '14px',
                                fontWeight: '600',
                                cursor: processingRequest === request.id ? 'not-allowed' : 'pointer',
                                opacity: processingRequest === request.id ? 0.7 : 1,
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                transition: 'all 0.3s ease'
                              }}
                            >
                              <XCircle size={14} />
                              Deny
                            </button>
                            <button
                              onClick={() => handleIncomingRequest(request.id, 'accept')}
                              disabled={processingRequest === request.id}
                              style={{
                                padding: '8px 16px',
                                background: '#00d4aa',
                                border: 'none',
                                borderRadius: '8px',
                                color: 'white',
                                fontSize: '14px',
                                fontWeight: '600',
                                cursor: processingRequest === request.id ? 'not-allowed' : 'pointer',
                                opacity: processingRequest === request.id ? 0.7 : 1,
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                transition: 'all 0.3s ease'
                              }}
                            >
                              <Check size={14} />
                              Accept
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Outgoing Requests Tab */}
              {activeTab === 'outgoing' && (
                <div>
                  {outgoingRequests.length === 0 ? (
                    <div style={{
                      textAlign: 'center',
                      padding: '60px 20px',
                      color: '#888'
                    }}>
                      <Clock size={48} style={{ marginBottom: '16px', opacity: 0.3 }} />
                      <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: '600' }}>
                        No pending requests
                      </h3>
                      <p style={{ margin: 0, fontSize: '14px' }}>
                        You haven't requested access to any private rooms
                      </p>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                      {outgoingRequests.map((request) => (
                        <div key={request.id} style={{
                          background: colors.bgCard,
                          border: '1px solid #333',
                          borderRadius: '12px',
                          padding: '20px'
                        }}>
                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            marginBottom: '16px'
                          }}>
                            <div style={{ flex: 1 }}>
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px',
                                marginBottom: '12px'
                              }}>
                                <div style={{
                                  width: '40px',
                                  height: '40px',
                                  background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                                  borderRadius: '50%',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}>
                                  <Eye size={20} style={{ color: 'white' }} />
                                </div>
                                <div>
                                  <h4 style={{
                                    margin: 0,
                                    fontSize: '16px',
                                    fontWeight: '600',
                                    color: '#e5e5e5'
                                  }}>
                                    {request.room?.title}
                                  </h4>
                                  <p style={{
                                    margin: 0,
                                    fontSize: '12px',
                                    color: '#888',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px'
                                  }}>
                                    <Calendar size={10} />
                                    Requested {new Date(request.joined_at).toLocaleDateString()}
                                  </p>
                                </div>
                              </div>
                              
                              <div style={{
                                padding: '12px',
                                background: colors.bgTertiary,
                                borderRadius: '8px',
                                marginBottom: '8px'
                              }}>
                                <p style={{
                                  margin: '0 0 4px 0',
                                  fontSize: '12px',
                                  color: '#888'
                                }}>
                                  Your message:
                                </p>
                                <p style={{
                                  margin: 0,
                                  fontSize: '14px',
                                  color: '#e5e5e5',
                                  lineHeight: '1.4'
                                }}>
                                  "{request.request_message}"
                                </p>
                              </div>
                              
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                              }}>
                                <div style={{
                                  padding: '4px 8px',
                                  background: '#f59e0b',
                                  borderRadius: '6px',
                                  fontSize: '10px',
                                  fontWeight: '600',
                                  color: 'white',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '4px'
                                }}>
                                  <Clock size={8} />
                                  PENDING
                                </div>
                                <span style={{ fontSize: '12px', color: '#888' }}>
                                  Hosted by {request.room?.host?.username}
                                </span>
                              </div>
                            </div>
                          </div>
                          
                          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <button
                              onClick={() => cancelOutgoingRequest(request.id)}
                              disabled={processingRequest === request.id}
                              style={{
                                padding: '8px 16px',
                                background: '#6b7280',
                                border: 'none',
                                borderRadius: '8px',
                                color: 'white',
                                fontSize: '14px',
                                fontWeight: '600',
                                cursor: processingRequest === request.id ? 'not-allowed' : 'pointer',
                                opacity: processingRequest === request.id ? 0.7 : 1,
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                transition: 'all 0.3s ease'
                              }}
                            >
                              <X size={14} />
                              Cancel Request
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}